'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Building2, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE } from '@/config/api.config';

export default function NewOrganizationPage() {
  const router = useRouter();
  const [name, setName] = useState('');
  const [sector, setSector] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Use cookie-based auth
        body: JSON.stringify({ name, sector, country }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's a limit error
        if (response.status === 403 && data.upgradeUrl) {
          setError(
            data.error + ' Please upgrade your plan to create more organizations.'
          );
        } else {
          setError(data.error || 'Failed to create organization');
        }
        setLoading(false);
        return;
      }

      // Success - redirect to organizations list
      router.push('/dashboard/organizations');
    } catch (err: any) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <Link
          href="/dashboard/organizations"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Create New Organization</h1>
          <p className="mt-2 text-gray-600">
            Set up an organization to manage your compliance projects
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-800">{error}</p>
              {error.includes('upgrade') && (
                <Link
                  href="/pricing"
                  className="mt-2 inline-flex items-center text-sm text-red-700 underline hover:text-red-900"
                >
                  View Plans & Pricing
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Acme Corporation"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">
                The legal name of your organization
              </p>
            </div>

            <div>
              <label htmlFor="sector" className="block text-sm font-medium text-gray-700 mb-2">
                Industry Sector <span className="text-red-500">*</span>
              </label>
              <select
                id="sector"
                required
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="">Select industry sector...</option>
                <option value="Technology">Technology & IT Services</option>
                <option value="Financial Services">Financial Services</option>
                <option value="Healthcare">Healthcare & Life Sciences</option>
                <option value="Education">Education</option>
                <option value="Government">Government & Public Sector</option>
                <option value="Retail">Retail & E-commerce</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Energy">Energy & Utilities</option>
                <option value="Telecommunications">Telecommunications</option>
                <option value="Transportation">Transportation & Logistics</option>
                <option value="Real Estate">Real Estate</option>
                <option value="Media">Media & Entertainment</option>
                <option value="Professional Services">Professional Services</option>
                <option value="Hospitality">Hospitality & Tourism</option>
                <option value="Non-Profit">Non-Profit & NGO</option>
                <option value="Other">Other</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Helps us provide sector-specific compliance guidance
              </p>
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                Country <span className="text-red-500">*</span>
              </label>
              <select
                id="country"
                required
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="">Select country...</option>
                <option value="AT">Austria</option>
                <option value="BE">Belgium</option>
                <option value="BG">Bulgaria</option>
                <option value="HR">Croatia</option>
                <option value="CY">Cyprus</option>
                <option value="CZ">Czech Republic</option>
                <option value="DK">Denmark</option>
                <option value="EE">Estonia</option>
                <option value="FI">Finland</option>
                <option value="FR">France</option>
                <option value="DE">Germany</option>
                <option value="GR">Greece</option>
                <option value="HU">Hungary</option>
                <option value="IE">Ireland</option>
                <option value="IT">Italy</option>
                <option value="LV">Latvia</option>
                <option value="LT">Lithuania</option>
                <option value="LU">Luxembourg</option>
                <option value="MT">Malta</option>
                <option value="NL">Netherlands</option>
                <option value="PL">Poland</option>
                <option value="PT">Portugal</option>
                <option value="RO">Romania</option>
                <option value="SK">Slovakia</option>
                <option value="SI">Slovenia</option>
                <option value="ES">Spain</option>
                <option value="SE">Sweden</option>
                <option value="GB">United Kingdom</option>
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="NZ">New Zealand</option>
                <option value="JP">Japan</option>
                <option value="SG">Singapore</option>
                <option value="OTHER">Other</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Primary jurisdiction for compliance requirements
              </p>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-start space-x-3 mb-6">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-600">
                  <p className="font-medium text-gray-900 mb-1">What happens next?</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Your organization will be created instantly</li>
                    <li>You can add multiple projects under this organization</li>
                    <li>Track compliance across all projects in one place</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-4">
                <Link
                  href="/dashboard/organizations"
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors text-center"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Organization'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
